import React, {useEffect, useState} from "react";
import moment from "moment";
import PropTypes from "prop-types";
import {FormattedMessage, useIntl} from "react-intl";

import {Button, TextField, Tooltip} from "@mui/material";

import StyledCard from "components/card/StyledCard";

import {getStatistics} from "api/personalShopper";

import {black500, greyishBlue500, greyishBlue600, primaryColor} from "assets/jss/main";

import {formatMoney} from "./formatters";

Tile.propTypes = {
    label: PropTypes.node.isRequired,
    value: PropTypes.node,
    detail: PropTypes.node,
    accent: PropTypes.bool,
};

function Tile({label, value, detail, accent = false}) {
    return (
        <StyledCard noHoverShadow style={{padding: "12px 16px", flex: "1 1 140px", minWidth: "140px"}}>
            <div style={{color: greyishBlue600, fontSize: "12px", fontWeight: 600, textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{label}</div>
            <div style={{color: accent ? primaryColor : black500, fontSize: "22px", fontWeight: 700, marginTop: "4px"}}>{value ?? "–"}</div>
            {detail && <div style={{color: greyishBlue500, fontSize: "11px", marginTop: "2px"}}>{detail}</div>}
        </StyledCard>
    );
}

/** The window of the statistics snapshot the Marketplace API keeps (its scheduled sync asks Sylius for it). */
const SNAPSHOT_DAYS = 30;

/**
 * The key figures of the last 30 days (the same ones as the Sylius dashboard, so both sides
 * show the same numbers): conversations, cards sent, click rate, assisted purchases and
 * revenue, and the return on the model's cost.
 */
function KpiHeader() {
    const intl = useIntl();
    const [statistics, setStatistics] = useState(null);
    // A window of the person's own (two days, "to" inclusive on screen): read live from Sylius.
    const [draft, setDraft] = useState({from: "", to: ""});
    const [range, setRange] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const query = range
            ? {from: `${range.from}T00:00:00`, to: range.to ? `${moment(range.to).add(1, "day").format("YYYY-MM-DD")}T00:00:00` : ""}
            : {days: SNAPSHOT_DAYS};
        getStatistics(query)
            .then((data) => !cancelled && setStatistics(data))
            .catch(() => !cancelled && setStatistics(null));

        return () => {
            cancelled = true;
        };
    }, [range]);

    const applyRange = () => setRange(draft.from ? {from: draft.from, to: draft.to && draft.to >= draft.from ? draft.to : ""} : null);

    const defined = (value) => value !== null && value !== undefined;
    const clickRate = statistics && defined(statistics.clickRate) ? `${Math.round(statistics.clickRate * 100)} %` : "–";
    const cost = statistics && defined(statistics.estimatedCostUsd) ? `$${statistics.estimatedCostUsd.toFixed(2)}` : "–";
    // Revenue in euros against a cost in dollars: an order of magnitude, as the Sylius dashboard shows it.
    const roi = statistics && defined(statistics.estimatedCostUsd) && statistics.estimatedCostUsd > 0 && defined(statistics.assistedGmv)
        ? `×${Math.round((statistics.assistedGmv / 100) / statistics.estimatedCostUsd)}`
        : "–";

    const footerParts = statistics ? [
        `${statistics.newConversations ?? 0} ${intl.formatMessage({id: "personalShopper.statistics.newConversations"})}`,
        `${statistics.turns ?? 0} ${intl.formatMessage({id: "personalShopper.statistics.turns"}).toLowerCase()}`,
        `${statistics.emailsCaptured ?? 0} ${intl.formatMessage({id: "personalShopper.statistics.emailsCaptured"})}`,
        `${statistics.escalations ?? 0} ${intl.formatMessage({id: "personalShopper.statistics.escalations"})}`,
    ] : [];
    if (statistics && defined(statistics.averageLatencyMs)) {
        footerParts.push(`${intl.formatMessage({id: "personalShopper.statistics.latency"})} ${(statistics.averageLatencyMs / 1000).toFixed(1)} s`);
    }
    // The per-model breakdown (provider, turns, tokens, cost) is a technical figure: it stays in
    // the Sylius back office, the team's header reads the business ones only.

    return (
        <div>
            <div style={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"}}>
                <div style={{fontWeight: 700, color: black500, fontSize: "15px"}}><FormattedMessage id="personalShopper.kpi.title" /></div>
                <span style={{color: greyishBlue600, fontSize: "13px"}}>
                    {range
                        ? <FormattedMessage id="personalShopper.kpi.range" values={{from: moment(range.from).format("DD/MM/YYYY"), to: moment(range.to || undefined).format("DD/MM/YYYY")}} />
                        : <FormattedMessage id="personalShopper.kpi.period" values={{days: SNAPSHOT_DAYS}} />}
                </span>
                <div style={{display: "flex", gap: "6px", alignItems: "center", marginLeft: "auto", flexWrap: "wrap"}}>
                    <TextField type="date" size="small" value={draft.from} onChange={(event) => setDraft({...draft, from: event.target.value})} inputProps={{style: {fontSize: "12px", padding: "6px 8px"}}} />
                    <TextField type="date" size="small" value={draft.to} onChange={(event) => setDraft({...draft, to: event.target.value})} inputProps={{style: {fontSize: "12px", padding: "6px 8px"}}} />
                    <Button size="small" variant="outlined" onClick={applyRange} disabled={!draft.from} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.kpi.apply" />
                    </Button>
                    {range && (
                        <Button size="small" onClick={() => {
                            setDraft({from: "", to: ""});
                            setRange(null);
                        }} sx={{textTransform: "none"}}>
                            <FormattedMessage id="personalShopper.kpi.reset" />
                        </Button>
                    )}
                </div>
            </div>
            <div style={{display: "flex", gap: "12px", flexWrap: "wrap"}}>
                <Tile label={<FormattedMessage id="personalShopper.kpi.conversations" />} value={statistics?.conversations} />
                <Tile label={<FormattedMessage id="personalShopper.kpi.productsSent" />} value={statistics?.productsSent} />
                <Tile
                    label={<FormattedMessage id="personalShopper.kpi.clickRate" />}
                    value={clickRate}
                    detail={statistics ? `${statistics.clicks ?? 0} ${intl.formatMessage({id: "personalShopper.statistics.clicks"})}` : null}
                />
                <Tile
                    label={<FormattedMessage id="personalShopper.kpi.purchases" />}
                    value={statistics?.assistedOrders}
                    detail={statistics ? `${intl.formatMessage({id: "personalShopper.kpi.revenue"})} ${formatMoney(statistics.assistedGmv ?? 0)}` : null}
                />
                <Tooltip title={<FormattedMessage id="personalShopper.kpi.roi.hint" />}>
                    <div style={{display: "flex", flex: "1 1 140px"}}>
                        <Tile
                            label={<FormattedMessage id="personalShopper.kpi.roi" />}
                            value={roi}
                            detail={`${intl.formatMessage({id: "personalShopper.kpi.cost"})} ${cost}`}
                            accent
                        />
                    </div>
                </Tooltip>
            </div>
            {footerParts.length > 0 && (
                <div style={{color: greyishBlue500, fontSize: "12px", marginTop: "8px"}}>{footerParts.join(" · ")}</div>
            )}
        </div>
    );
}

export default KpiHeader;
